import { useEffect, useRef, useState } from 'react'

/**
 * 儀表板用的小圖表。全部手刻 inline SVG，不引入圖表套件——
 * 這裡只有長條圖，套件帶來的 bundle 成本換不到對等的好處，
 * 而且 SVG 走 @media print 也能直接印出來。
 *
 * 共通做法：
 *   - viewBox 的寬度＝容器實際像素寬（見 useChartWidth），1 單位 = 1px。
 *     不能用固定寬度的 viewBox：那樣整張圖會等比縮放，同一個 11px 的字
 *     在窄面板裡只剩 5px、在寬螢幕上卻脹成 29px。
 *   - 每根長條都掛 <title>，滑鼠停留就有原生 tooltip，不必寫 JS。
 *   - 數值直接標在圖上，識別不單靠顏色（色盲、黑白列印都讀得到）。
 */

/** 量測容器寬度，讓 SVG 的座標單位等於 CSS px，字級才不會跟著面板寬度伸縮 */
function useChartWidth(fallback = 600) {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(fallback)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      // 下限只是避免首次量到 0，不能訂太高——一旦高於實際寬度，
      // SVG 又會被等比縮小，字級就跟著失真
      setWidth(Math.max(160, Math.round(entry.contentRect.width)))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return [ref, width] as const
}

/**
 * 把座標軸上限進位到好讀的整數，格線才不會落在 137 這種數字上。
 * 級距切得比 1／2／5 細，否則最高值 142 會被推到 200，長條只剩七成高度。
 */
const STEPS = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]
function niceMax(v: number): number {
  if (v <= 0) return 1
  const mag = Math.pow(10, Math.floor(Math.log10(v)))
  const n = v / mag
  return (STEPS.find((s) => n <= s) ?? 10) * mag
}

const PAD_L = 44
const AXIS_FONT = 12

/** 三條水平格線（0／中／上限）＋刻度數字 */
function Grid({ w, padT, plotH, max, format }: {
  w: number; padT: number; plotH: number; max: number; format: (n: number) => string
}) {
  const ticks = [0, 0.5, 1]
  return (
    <>
      {ticks.map((t) => {
        const y = padT + plotH * (1 - t)
        return (
          <g key={t}>
            <line
              x1={PAD_L - 6}
              y1={y}
              x2={w}
              y2={y}
              stroke={t === 0 ? 'var(--line)' : 'var(--chart-grid)'}
              strokeWidth="1"
            />
            <text x={PAD_L - 10} y={y + 4} fontSize={AXIS_FONT} fill="var(--ink-soft)" textAnchor="end">
              {format(max * t)}
            </text>
          </g>
        )
      })}
    </>
  )
}

/**
 * 疊在長條上的折線。用中性墨色，不另外佔用一個色相；線下先描一圈底色，
 * 壓在深色長條上也切得開。null 代表該點沒有資料，線在那裡斷開。
 */
function TrendLine({ points }: { points: Array<{ x: number; y: number } | null> }) {
  const path = points
    .map((p, i) => (p ? `${i === 0 || !points[i - 1] ? 'M' : 'L'}${p.x} ${p.y}` : ''))
    .join(' ')
    .trim()
  if (!path) return null
  return (
    <>
      <path d={path} fill="none" stroke="var(--panel)" strokeWidth="4" strokeLinejoin="round" />
      <path d={path} fill="none" stroke="var(--chart-line)" strokeWidth="2" strokeLinejoin="round" />
      {points.map((p, i) =>
        p ? <circle key={i} cx={p.x} cy={p.y} r="3" fill="var(--chart-line)" stroke="var(--panel)" strokeWidth="1.5" /> : null
      )}
    </>
  )
}

function TrendLegend({ label, extra }: { label: string; extra?: React.ReactNode }) {
  return (
    <div className="chart-legend">
      {extra}
      <span>
        <i className="line" />
        {label}
      </span>
    </div>
  )
}

export interface Series {
  key: string
  label: string
  color: string
}

/**
 * 堆疊直條圖，可再疊一條折線。用在「總量的走勢」與「組成比重的變化」
 * 要一起看的時候，堆起來的總高度就是總量，不必再另外畫一張總數圖。
 */
export function StackedColumnChart({
  data,
  series,
  height = 130,
  axisLabel,
  showXLabels = false,
  format = (n) => String(n),
  line,
  topLabel,
  ariaLabel,
}: {
  data: Array<{ label: string; values: Record<string, number>; hint?: string }>
  series: Series[]
  height?: number
  axisLabel?: string
  showXLabels?: boolean
  format?: (n: number) => string
  /**
   * 疊在長條上的折線，與長條共用同一條 Y 軸。null 的點會讓線斷開。
   * 只接受同一個尺度的數值——要畫別的量就得開第二條軸，那條路不走。
   */
  line?: { label: string; values: Array<number | null> }
  /**
   * 每根長條頂端的註記。用來塞「沒有自己座標軸的第二個量」——
   * 例如長條是金額時，在上面標當月篇數，就不必為了篇數再開一條 Y 軸。
   * 給了這個就不再標最高點的總額，免得一根長條上兩個數字。
   */
  topLabel?: (index: number) => string | null
  ariaLabel: string
}) {
  const [ref, w] = useChartWidth()
  // 有軸標題時上緣要留得夠開：標題在 y=12，最上面那條格線的刻度數字
  // 緊接在 padT 下方，兩者間距不足 20px 就會黏在一起
  const padT = axisLabel ? 34 : 8
  const padB = showXLabels ? 20 : 6
  const plotH = Math.max(1, height - padT - padB)
  const totals = data.map((d) => series.reduce((s, se) => s + (d.values[se.key] ?? 0), 0))
  const max = niceMax(Math.max(...totals, 0))
  const slot = (w - PAD_L) / Math.max(1, data.length)
  const bw = Math.max(6, Math.min(64, slot * 0.62))
  const y0 = padT + plotH
  const peak = totals.reduce((best, v, i) => (v > totals[best] ? i : best), 0)
  const cx = (i: number) => PAD_L + i * slot + slot / 2
  // 欄位太窄時每隔一格才標月份，否則 12 個標籤會疊在一起
  const labelStep = slot < 28 ? 2 : 1
  const gap = 2 // 分段之間留一道底色縫，相鄰兩段才不會糊成一塊

  return (
    <>
      <div ref={ref}>
        <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} role="img" aria-label={ariaLabel}>
          {axisLabel && <text x="0" y="12" fontSize={AXIS_FONT} fill="var(--ink-soft)">{axisLabel}</text>}
          <Grid w={w} padT={padT} plotH={plotH} max={max} format={format} />

          {data.map((d, i) => {
            const x = cx(i) - bw / 2
            let cursor = y0
            return (
              <g key={d.label}>
                {series.map((se) => {
                  const v = d.values[se.key] ?? 0
                  if (v <= 0) return null
                  const h = (v / max) * plotH
                  const y = cursor - h
                  cursor = y - gap
                  return <rect key={se.key} x={x} y={y} width={bw} height={h} rx="3" fill={se.color} />
                })}
                <rect x={x} y={padT} width={bw} height={plotH} fill="transparent">
                  <title>
                    {d.hint ??
                      `${d.label}：${series.map((se) => `${se.label} ${format(d.values[se.key] ?? 0)}`).join('、')}`}
                  </title>
                </rect>
                {topLabel
                  ? topLabel(i) && (
                      <text x={cx(i)} y={cursor - 3} fontSize="11" fill="var(--ink-soft)" textAnchor="middle">
                        {topLabel(i)}
                      </text>
                    )
                  : i === peak && totals[i] > 0 && (
                      <text x={cx(i)} y={cursor - 3} fontSize={AXIS_FONT} fill="var(--ink)" textAnchor="middle">
                        {format(totals[i])}
                      </text>
                    )}
                {showXLabels && i % labelStep === 0 && (
                  <text x={cx(i)} y={height - 5} fontSize={AXIS_FONT} fill="var(--ink-soft)" textAnchor="middle">
                    {d.label}
                  </text>
                )}
              </g>
            )
          })}

          {line && (
            <TrendLine
              points={line.values.map((v, i) => (v === null ? null : { x: cx(i), y: y0 - (v / max) * plotH }))}
            />
          )}
        </svg>
      </div>
      {line ? (
        <TrendLegend
          label={line.label}
          extra={series.map((se) => (
            <span key={se.key}>
              <i style={{ background: se.color }} />
              {se.label}
            </span>
          ))}
        />
      ) : (
        <div className="chart-legend">
          {series.map((se) => (
            <span key={se.key}>
              <i style={{ background: se.color }} />
              {se.label}
            </span>
          ))}
        </div>
      )}
    </>
  )
}

/**
 * 熱力表格子的底色。濃度只走 6%～55%——
 * 再深下去，中間色階不論配深色字或白字，對比度都掉到 WCAG AA 4.5:1 以下，
 * 所以寧可色階淺一點，讓數字在每一格都讀得清楚。
 */
function heatStyle(value: number, max: number) {
  if (!value || max <= 0) return undefined
  return { background: `rgba(var(--chart-heat-rgb), ${(0.06 + (value / max) * 0.49).toFixed(3)})` }
}

export interface HeatRow {
  name: string
  sub?: string
  cells: Record<string, number>
  /** 該期別是否超標；真時在數字後加 ▲，不靠顏色單獨表意 */
  over?: (bucket: string) => boolean
}

/**
 * 期別對照熱力表：列＝對象、欄＝連續期別，格子底色深淺代表數量多寡。
 *
 * 底色只是輔助，數字一定照寫——對象一多，堆疊或多色圖就分不清了，
 * 表格則是加幾列的事。
 */
export function HeatTable({
  head,
  buckets,
  bucketLabel,
  rows,
}: {
  head: string
  buckets: string[]
  bucketLabel: (bucket: string) => string
  rows: HeatRow[]
}) {
  const max = Math.max(...rows.flatMap((r) => buckets.map((b) => r.cells[b] ?? 0)), 0)
  const totals = buckets.map((b) => rows.reduce((s, r) => s + (r.cells[b] ?? 0), 0))

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <tr>
            <th>{head}</th>
            {buckets.map((b) => (
              <th key={b} className="num">{bucketLabel(b)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name}>
              <td>
                {r.name} {r.sub && <span style={{ color: 'var(--ink-soft)' }}>{r.sub}</span>}
              </td>
              {buckets.map((b) => {
                const v = r.cells[b] ?? 0
                if (!v) return <td key={b} className="num" style={{ color: 'var(--line-strong)' }}>—</td>
                return (
                  <td key={b} className="num" style={heatStyle(v, max)}>
                    {v}
                    {r.over?.(b) && <span style={{ color: 'var(--bad)' }}> ▲</span>}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
        <tfoot>
          {/* 合計是另一個量級，跟著上色會把整列都塗成最深 */}
          <tr>
            <td>合計</td>
            {totals.map((n, i) => (
              <td key={buckets[i]} className="num">{n || '—'}</td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

export interface Segment {
  label: string
  value: number
  color: string
}

/** 單條堆疊長條 + 圖例。兩三個分項用這個，不要畫圓餅 */
export function StackedBar({ data, format = (n) => String(n) }: { data: Segment[]; format?: (n: number) => string }) {
  const total = data.reduce((s, d) => s + d.value, 0)
  if (total <= 0) return null

  return (
    <>
      <div className="stackbar" role="img"
        aria-label={data.map((d) => `${d.label} ${format(d.value)}`).join('，')}>
        {data.map((d) => (
          <i
            key={d.label}
            style={{ width: `${(d.value / total) * 100}%`, background: d.color }}
            title={`${d.label}：${format(d.value)}（${Math.round((d.value / total) * 100)}%）`}
          />
        ))}
      </div>
      <div className="chart-legend">
        {data.map((d) => (
          <span key={d.label}>
            <i style={{ background: d.color }} />
            {d.label} {format(d.value)}（{Math.round((d.value / total) * 100)}%）
          </span>
        ))}
      </div>
    </>
  )
}
